import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Appointment, SeriesRule } from "./types";
import { weekDays, slotToInstant, minutesBetween, SLOT_MIN, fmtHm } from "./time";
import AppointmentBlock, { APPT_MIME, SLOT_H, PX_PER_MIN } from "./AppointmentBlock";
import "./Calendar.css";

interface Props {
  anchor: Date;
  workStart: string; // "HH:MM"
  workEnd: string; // "HH:MM"
  appointments: Appointment[];
  series: Record<string, SeriesRule>;
  patientNames: Record<string, string>;
  onMove: (id: string, when: Date) => void;
  onResize: (id: string, durationMin: number) => void;
  onSelect: (a: Appointment) => void;
  onCreateAt: (when: Date) => void;
  /** Press-and-drag on empty cells: create at `when` lasting `durationMin`. */
  onCreateRange: (when: Date, durationMin: number) => void;
}

/** Vertical drag-to-create selection, confined to one day column. */
type DragSel = { dayIdx: number; anchorSlot: number; endSlot: number };

function acceptAppt(e: React.DragEvent) {
  if (Array.from(e.dataTransfer.types).includes(APPT_MIME)) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
}

function hmToMinutes(hm: string): number {
  const [h, m] = hm.split(":").map(Number);
  return h * 60 + m;
}

/** Two-lane layout for overlapping blocks; 3+ overlaps share lane 1. */
function assignLanes(dayAppts: Appointment[]): Map<string, { lane: number; laneCount: number }> {
  const sorted = [...dayAppts].sort((a, b) => a.start_at.localeCompare(b.start_at));
  const out = new Map<string, { lane: number; laneCount: number }>();
  let lastLane0End = "";
  for (const a of sorted) {
    const lane = a.start_at < lastLane0End ? 1 : 0;
    if (lane === 0) lastLane0End = a.end_at;
    out.set(a.appointment_id, { lane, laneCount: 1 });
  }
  // Widen only blocks that actually overlap something.
  for (const a of sorted) {
    for (const b of sorted) {
      if (a === b) continue;
      if (a.start_at < b.end_at && a.end_at > b.start_at) {
        out.get(a.appointment_id)!.laneCount = 2;
        out.get(b.appointment_id)!.laneCount = 2;
      }
    }
  }
  return out;
}

export default function WeekGrid({
  anchor, workStart, workEnd, appointments, series, patientNames,
  onMove, onResize, onSelect, onCreateAt, onCreateRange,
}: Props) {
  const { i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const [drag, setDrag] = useState<DragSel | null>(null);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const days = useMemo(() => weekDays(anchor), [anchor]);
  const slotCount = Math.max(1, (hmToMinutes(workEnd) - hmToMinutes(workStart)) / SLOT_MIN);
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: "short" }),
    [i18n.language],
  );

  const byDay = useMemo(() => {
    const map: Appointment[][] = days.map(() => []);
    for (const a of appointments) {
      const start = new Date(a.start_at);
      const idx = days.findIndex(
        (d) =>
          d.getFullYear() === start.getFullYear() &&
          d.getMonth() === start.getMonth() &&
          d.getDate() === start.getDate(),
      );
      if (idx >= 0) map[idx].push(a);
    }
    return map;
  }, [appointments, days]);

  const today = new Date();
  const isToday = (d: Date) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();

  return (
    <div className="cal-grid" style={{ gridTemplateColumns: `48px repeat(7, 1fr)` }}>
      <div className="cal-corner" />
      {days.map((d, i) => (
        <div key={i} className={`cal-dayhead${isToday(d) ? " cal-dayhead--today" : ""}`}>
          {dayFmt.format(d)} {d.getDate()}
        </div>
      ))}

      <div className="cal-gutter">
        {Array.from({ length: slotCount }, (_, s) =>
          s % 4 === 0 ? (
            <div key={s} className="cal-gutter-label" style={{ top: s * SLOT_H }}>
              {fmtHm(slotToInstant(days[0], workStart, s))}
            </div>
          ) : null,
        )}
      </div>

      {days.map((day, dayIdx) => {
        const lanes = assignLanes(byDay[dayIdx]);
        const workStartInstant = slotToInstant(day, workStart, 0);
        const nowOffsetMin = minutesBetween(workStartInstant, now);
        const showNowLine =
          isToday(day) && nowOffsetMin >= 0 && nowOffsetMin <= slotCount * SLOT_MIN;
        return (
          <div
            key={dayIdx}
            className={`cal-daycol${dayIdx >= 5 ? " cal-daycol--weekend" : ""}`}
            style={{ height: slotCount * SLOT_H }}
            onPointerDown={(e) => {
              // Start a drag-to-create only from an empty cell with the main
              // button; blocks handle their own pointer events (move/resize).
              const target = e.target as HTMLElement;
              if (e.button !== 0 || !target.classList.contains("cal-cell")) return;
              const slot = Number(target.dataset.slot);
              if (Number.isNaN(slot)) return;
              // Capture so the whole gesture stays on this column even when
              // the pointer leaves it. Optional-called: jsdom lacks it.
              (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
              setDrag({ dayIdx, anchorSlot: slot, endSlot: slot });
            }}
            onPointerMove={(e) => {
              if (!drag || drag.dayIdx !== dayIdx) return;
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const slot = Math.max(
                0,
                Math.min(slotCount - 1, Math.floor((e.clientY - rect.top) / SLOT_H)),
              );
              if (slot !== drag.endSlot) setDrag({ ...drag, endSlot: slot });
            }}
            onPointerUp={() => {
              if (!drag || drag.dayIdx !== dayIdx) return;
              setDrag(null);
              // A press without vertical travel is a plain click — creation
              // by click stays on double-click to avoid accidental dialogs.
              if (drag.endSlot === drag.anchorSlot) return;
              const from = Math.min(drag.anchorSlot, drag.endSlot);
              const span = Math.abs(drag.endSlot - drag.anchorSlot) + 1;
              onCreateRange(slotToInstant(day, workStart, from), span * SLOT_MIN);
            }}
            onPointerCancel={() => setDrag(null)}
            onDoubleClick={(e) => {
              // Lives on the column, not the cell: pointer capture (above)
              // retargets click/dblclick to the captured column, so a cell
              // handler never fires in real browsers. Ignore dblclicks that
              // bubbled from a block.
              const target = e.target as HTMLElement;
              if (target !== e.currentTarget && !target.classList.contains("cal-cell")) return;
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const slot = Math.max(
                0,
                Math.min(slotCount - 1, Math.floor((e.clientY - rect.top) / SLOT_H)),
              );
              onCreateAt(slotToInstant(day, workStart, slot));
            }}
          >
            {Array.from({ length: slotCount }, (_, slotIdx) => (
              <div
                key={slotIdx}
                data-testid={`cell-${dayIdx}-${slotIdx}`}
                data-slot={slotIdx}
                className={`cal-cell${slotIdx % 4 === 3 ? " cal-cell--hour" : ""}`}
                onDragOver={acceptAppt}
                onDrop={(e) => {
                  const id = e.dataTransfer.getData(APPT_MIME);
                  if (!id) return;
                  e.preventDefault();
                  onMove(id, slotToInstant(day, workStart, slotIdx));
                }}
              />
            ))}
            {drag && drag.dayIdx === dayIdx && (
              <div
                className="cal-drag-ghost"
                data-testid="drag-ghost"
                style={{
                  top: Math.min(drag.anchorSlot, drag.endSlot) * SLOT_H,
                  height: (Math.abs(drag.endSlot - drag.anchorSlot) + 1) * SLOT_H,
                }}
              >
                {fmtHm(slotToInstant(day, workStart, Math.min(drag.anchorSlot, drag.endSlot)))}
                –
                {fmtHm(slotToInstant(day, workStart, Math.max(drag.anchorSlot, drag.endSlot) + 1))}
              </div>
            )}
            {byDay[dayIdx].map((a) => {
              const start = new Date(a.start_at);
              const end = new Date(a.end_at);
              // Clamp: an appointment outside the configured work window must
              // stay visible at the grid edge, not render off-column (M1-#6).
              const top = Math.max(0, minutesBetween(workStartInstant, start) * PX_PER_MIN);
              const height = Math.max(SLOT_H, minutesBetween(start, end) * PX_PER_MIN);
              const { lane, laneCount } = lanes.get(a.appointment_id)!;
              return (
                <AppointmentBlock
                  key={a.appointment_id}
                  appointment={a}
                  patientName={patientNames[a.patient_id] || a.patient_id}
                  isRecurring={Boolean(a.series_id && series[a.series_id])}
                  top={top}
                  height={height}
                  lane={lane}
                  laneCount={laneCount}
                  onSelect={onSelect}
                  onResize={onResize}
                />
              );
            })}
            {showNowLine && (
              <div className="cal-now-line" style={{ top: nowOffsetMin * PX_PER_MIN }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
